import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Shield, UserX, XCircle } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card';
import { Input } from '~/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { apiClient } from '~/lib/api';
import { formatDateTime } from '~/lib/utils';

export default function UsersPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['admin-users', page, search],
    queryFn: () =>
      apiClient.getUsers({
        page,
        limit: 20,
        search: search || undefined,
      }),
  });

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, updateData }: { userId: string; updateData: { isActive?: boolean; isAdmin?: boolean } }) =>
      apiClient.updateUserStatus(userId, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className='space-y-6'>
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-3xl font-bold'>User Management</h1>
          <p className='text-muted-foreground'>View and manage user accounts</p>
        </div>
        <form onSubmit={handleSearch} className='flex items-center gap-2'>
          <Input
            placeholder='Search by email, username...'
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className='w-[300px]'
          />
          <Button type='submit'>Search</Button>
        </form>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Users List</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='text-center py-8'>
              <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4'></div>
              <p className='text-muted-foreground'>Loading users...</p>
            </div>
          ) : data?.data?.items.length === 0 ? (
            <div className='text-center py-8'>
              <p className='text-muted-foreground'>No users found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Activity</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data?.data?.items.map((user: any) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          {user.avatarUrl ? (
                            <img
                              src={user.avatarUrl}
                              alt={user.username || user.displayName}
                              className='h-8 w-8 rounded-full'
                            />
                          ) : (
                            <div className='h-8 w-8 rounded-full bg-primary flex items-center justify-center text-primary-foreground text-xs font-semibold'>
                              {(user.username || user.displayName || user.email)?.[0]?.toUpperCase()}
                            </div>
                          )}
                          <div>
                            <div className='font-medium'>{user.username || user.displayName || 'No name'}</div>
                            {user.displayName && user.username && (
                              <div className='text-xs text-muted-foreground'>@{user.username}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-2'>
                          <span className='text-sm'>{user.email}</span>
                          {user.emailVerified ? (
                            <CheckCircle className='h-4 w-4 text-green-600' />
                          ) : (
                            <XCircle className='h-4 w-4 text-muted-foreground' />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.isActive ? (
                          <Badge variant='default'>Active</Badge>
                        ) : (
                          <Badge variant='destructive'>Banned</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {user.isAdmin ? (
                          <Badge variant='default' className='bg-purple-600'>
                            <Shield className='h-3 w-3 mr-1' />
                            Admin
                          </Badge>
                        ) : (
                          <Badge variant='secondary'>User</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className='text-sm text-muted-foreground'>
                          <div>{user._count.videoComments} comments</div>
                          <div>{user._count.videoRatings} ratings</div>
                          <div>{user._count.savedVideos} saved</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className='text-sm text-muted-foreground'>{formatDateTime(user.createdAt)}</div>
                      </TableCell>
                      <TableCell>
                        <div className='flex flex-col gap-2'>
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() =>
                              updateUserMutation.mutate({
                                userId: user.id,
                                updateData: { isActive: !user.isActive },
                              })
                            }
                            disabled={updateUserMutation.isPending}
                            className='w-full'
                          >
                            {user.isActive ? (
                              <>
                                <UserX className='h-4 w-4 mr-1' />
                                Ban
                              </>
                            ) : (
                              <>
                                <CheckCircle className='h-4 w-4 mr-1' />
                                Unban
                              </>
                            )}
                          </Button>
                          <Button
                            size='sm'
                            variant={user.isAdmin ? 'secondary' : 'default'}
                            onClick={() =>
                              updateUserMutation.mutate({
                                userId: user.id,
                                updateData: { isAdmin: !user.isAdmin },
                              })
                            }
                            disabled={updateUserMutation.isPending}
                            className='w-full'
                          >
                            {user.isAdmin ? (
                              <>
                                <XCircle className='h-4 w-4 mr-1' />
                                Remove Admin
                              </>
                            ) : (
                              <>
                                <Shield className='h-4 w-4 mr-1' />
                                Make Admin
                              </>
                            )}
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {data?.data && data.data.totalPages > 1 && (
                <div className='flex items-center justify-between mt-4'>
                  <div className='text-sm text-muted-foreground'>
                    Showing {(page - 1) * 20 + 1}-{Math.min(page * 20, data.data.total)} of {data.data.total} users
                  </div>
                  <div className='flex items-center gap-2'>
                    <Button variant='outline' size='sm' onClick={() => setPage(page - 1)} disabled={page === 1}>
                      Previous
                    </Button>
                    <div className='text-sm'>
                      Page {page} of {data.data.totalPages}
                    </div>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() => setPage(page + 1)}
                      disabled={page === data.data.totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
